// pages/OrderDetails.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchOrderDetails } from '../utils/api';
import { displayPrice, toTitleCase } from '../utils/general';
import { useSettings } from '../context/SettingsContext';
import CancelOrderButton from '../components/CancelOrderButton';
import PrintReceipt from '../components/sendToPrinter';
// import PayOrderButton from './CheckoutPage';
import { OrderSummary, OrderItem, Tables, TableDetails } from '../utils/types';
import { FaArrowLeftLong } from "react-icons/fa6";
import { useCart } from '../context/CartContext'; // Import the cart context
import ReactDOM from 'react-dom';
import { BsThreeDots } from "react-icons/bs";
import MoreOrderNavModal from '../components/moreOrderNav'; // Adjust the import path as needed


const OrderDetails: React.FC = () => {
    const { orderId } = useParams<{ orderId: any }>();
    // const vendorId = JSON.parse(localStorage.getItem('user') || '{}').vendor_id;

    const navigate = useNavigate();
    const { settings } = useSettings();
    const { cartItems, updateQuantity, removeFromCart, clearCart, setCartItems } = useCart();

    const [order, setOrder] = useState<OrderSummary | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    // const [moreOrderNavModalOpen, setMoreOrderNavModalOpen] = useState(false); // State for modal visibility

    const [isModalVisible, setIsModalVisible] = useState(false);

    const handleIsModalVisible = () => {
        setIsModalVisible(true);
    };

    const closeIsModalVisible = () => {
        setIsModalVisible(false);
    };

    const [printModalOpen, setPrintModalOpen] = useState(false); // State for modal visibility

    const handlePrintButtonClick = () => {
        setIsModalVisible(false); // Close the current modal
        setPrintModalOpen(true); // Open the print modal
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setPrintModalOpen(false);
    };
    useEffect(() => {

        // localStorage.removeItem('existingOrderId'); // Clear existing order ID to start a new order
        // localStorage.removeItem('checkedOutItems');
        // localStorage.removeItem('selectedOrderType');
        // localStorage.removeItem('selectedTable');
        // localStorage.removeItem('orderId');

        // localStorage.setItem('orderId', orderId);

        const fetchOrder = async () => {
            try {
                if (settings && orderId) {
                    const orderDetails = await fetchOrderDetails(settings.vendorId, orderId);
                    // console.log('orderDetails page: ', orderDetails);

                    setOrder(orderDetails);
                }
            } catch (error) {
                console.error("Error fetching order details:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [settings, orderId]);

    if (loading) return <p>Loading order details...</p>;
    if (!order) return <p>Order not found</p>;

    // Calculate the item price including add-ons, toppings, and specifications
    const calculateItemPrice = (item: any) => {
        // Base item price (default to 0 if not provided)
        const basePrice = parseFloat(item.price) || 0;

        // Parse add-ons, toppings, and specifications
        const parsedItems = JSON.parse(item.items);

        // Ensure addonDetails is an array; calculate total add-on cost
        const addOnCost = Array.isArray(parsedItems.addonDetails)
            ? parsedItems.addonDetails.reduce((total: number, addon: any) => {
                return total + (parseFloat(addon.price) || 0); // Use add-on price if available, otherwise 0
            }, 0)
            : 0;

        // Ensure toppingDetails is an array; calculate total toppings cost
        const toppingsCost = Array.isArray(parsedItems.toppingDetails)
            ? parsedItems.toppingDetails.reduce((total: number, topping: any) => {
                return total + (parseFloat(topping.price) || 0); // Use topping price if available, otherwise 0
            }, 0)
            : 0;

        // Ensure specifications is an array; calculate total specifications cost
        const specificationCost = Array.isArray(parsedItems.specifications)
            ? parsedItems.specifications.reduce((total: number, spec: any) => {
                return total + (parseFloat(spec.extra_cost) || 0); // Use specification extra cost if available, otherwise 0
            }, 0)
            : 0;

        // Calculate total item price by summing up all costs
        return basePrice + addOnCost + toppingsCost + specificationCost;
    };

    // Assuming taxSettings is an array of objects with the structure you're working with:
    const taxSettings = JSON.parse(localStorage.getItem('taxsettings') || '[]');

    // Define the tax object properties type
    interface TaxSetting {
        vendorId: number;
        taxId: number;
        type: 'percentage' | 'fixed'; // Assuming only 'percentage' and 'fixed' are possible values
        taxAmount: string; // If stored as a string, otherwise use `number`
        name: string;
    }

    // Safely extract tax properties if taxSettings[0] exists
    const firstTax = (taxSettings && taxSettings[0]) as TaxSetting | undefined;

    // Initialize the tax array with the expected tuple type
    const tax: [number, string, number, string] = [
        firstTax?.taxId || 0,
        firstTax?.type || 'percentage',
        parseFloat(firstTax?.taxAmount || '0'),
        firstTax?.name || ''
    ];

    // Define the taxCalculate function with proper TypeScript type annotations
    const taxCalculate = (amountToPay: number, tax: [number, string, number, string]): [number, number] => {
        let taxAmount = 0;
        let totalAmount = 0;

        if (tax[1] === 'percentage') {
            taxAmount = (amountToPay * tax[2]) / 100;
            totalAmount = amountToPay + taxAmount;
        } else {
            taxAmount = tax[2];
            totalAmount = amountToPay + taxAmount;
        }

        return [taxAmount, totalAmount];
    };

    // Example of using the taxCalculate function
    const amountToPay: number = parseFloat(order.amount_to_pay) || 0; // Example amount
    const [calculatedTax, calculatedTotal] = taxCalculate(amountToPay, tax);

    const handleBackNavigation = () => {
        const referrer = document.referrer;
        const isSameDomain = referrer && new URL(referrer).origin === window.location.origin;

        if (isSameDomain) {
            // Navigate back if the referrer is within the same domain
            navigate(-1);
        } else {
            // Otherwise, navigate to the home page
            navigate(-1);
        }
    };

    // console.log('order in OrderDetails: ', order);

    // Function to close the modal
    // const closeModal = () => setPrintModalOpen(false);

    const combineItems = (items: OrderItem[]) => {
        const combinedItemsMap = new Map();

        items.forEach((item: OrderItem) => {
            const key = `${item.product_id}-${item.add_ons}-${item.toppings}-${JSON.stringify(item.specifications)}`;

            if (combinedItemsMap.has(key)) {
                const existingItem = combinedItemsMap.get(key);
                existingItem.quantity += item.quantity;
                existingItem.total_price = (parseFloat(existingItem.total_price) + parseFloat(item.total_price)).toFixed(2);
            } else {
                combinedItemsMap.set(key, { ...item });
            }
        });

        const combinedItems = Array.from(combinedItemsMap.values());
        return combinedItems;
    };

    const combinedItems = combineItems(order.items);

    // console.log('combinedItems: ', combinedItems);

    return (
        <div className="app-page">

            <div className='w-full overflow-x-hidden overflow-y-auto pr-4 mt-22 mb-28'>



                <div className='w-screen px-2 py-2 sticky top-0 bg-gray-100  flex flex-col justify-start items-start border border-gray-300  rounded-b-2xl shadow-xl mb-5'>

                    {/* Modal header */}
                    <div className='w-full flex flex-row justify-between '>

                        <button
                            onClick={handleBackNavigation}
                            className="text-lg text-blue-600  p-1"
                            aria-label="Close"
                        >
                            <FaArrowLeftLong />
                        </button>

                    </div>

                    <div className='w-full px-2 flex flex-row justify-between'>
                        <h4 className='pageTitle'>Order {order.id} Details</h4>
                        {/* <h4 className='text-white text-sm ActionBtn bg-green-500 text-center'
                            onClick={() => setPrintModalOpen(true)}
                        >
                            Print<br />{order.id}</h4> */}
                    </div>

                    <div className='w-full flex flex-row justify-end font-semibold p-2' style={{ fontSize: '0.9rem', color: '#7a75b5' }} >
                        <p>Status: <span className={`font-bold  mr-4 ${order.status === 'cancel' ? 'text-red-500' : 'text-green-500'}`}>{order.status.toUpperCase()}</span></p>

                        <p>{order.order_type}</p>

                        {order.table_id !== undefined && order.table_id !== null && order.table_id !== 0 && (
                            <p className='ml-4'>Table: {order.table?.table_name} at {order.table?.location}</p>
                        )}
                    </div>
                </div>

                <div className='w-full h-72 md:h-72 m-2 b-8'>
                    {/* <h2 className='my-2 border-0 border-t-2 border-t-gray-400'>Items:</h2> */}
                    <ol className='m-0 list-inside list-decimal pb-8'>

                        {/* Check if order.items is an array before mapping */}
                        {Array.isArray(order.items) ? (
                            order.items.map((item, index) => (


                                <li key={index} className='flex flex-col w-full mb-4 border-b border-gray-300 pb-2'>
                                    <h4 className="text-md font-semibold">
                                        {toTitleCase(item.name)}
                                        {/* {settings?.currencyName} */}
                                        {/* {displayPrice(item.price)} */}
                                    </h4>

                                    {/* Display Add-ons */}
                                    <ul className='text-sm list-disc ml-5'>
                                        {Array.isArray(JSON.parse(item.items)?.addonDetails) &&
                                            JSON.parse(item.items).addonDetails.map((addon: any, addonIndex: number) => (
                                                <li key={addonIndex}>
                                                    {addon.name}
                                                    {/* &nbsp;-&nbsp;
                                                    {settings?.currencyName}
                                                    {displayPrice(addon.price)} */}

                                                    {addon.price > 0 && (
                                                        <>&nbsp;( +{settings?.currencyName}{displayPrice(addon.price)})</>
                                                    )}
                                                </li>
                                            ))}
                                    </ul>

                                    {/* Display Toppings */}
                                    <ul className='text-sm list-disc ml-5'>
                                        {Array.isArray(JSON.parse(item.items)?.toppingDetails) &&
                                            JSON.parse(item.items).toppingDetails.map((topping: any, toppingIndex: number) => (
                                                <li key={toppingIndex}>
                                                    {topping.name}
                                                    {/* &nbsp;-&nbsp;
                                                    {settings?.currencyName}
                                                    {displayPrice(topping.price)} */}
                                                    {topping.price > 0 && (
                                                        <>&nbsp;( +{settings?.currencyName}{displayPrice(topping.price)})</>
                                                    )}
                                                </li>
                                            ))}
                                    </ul>

                                    {/* Display Specifications */}
                                    {
                                        Array.isArray(JSON.parse(item.items)?.specifications) &&
                                            JSON.parse(item.items).specifications.length > 0 ? (
                                            <ul className='text-sm list-disc ml-5'>
                                                {JSON.parse(item.items).specifications.map((spec: any, specIndex: number) => (
                                                    <li key={specIndex}>
                                                        {spec.option_name}
                                                        {/* &nbsp;-&nbsp;
                                                        {settings?.currencyName}
                                                        {displayPrice(spec.extra_cost)} */}
                                                        {spec.extra_cost > 0 && (
                                                            <>&nbsp;( +{settings?.currencyName}{displayPrice(spec.extra_cost)})</>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p>&nbsp;</p>
                                        )
                                    }

                                    {
                                        //     Array.isArray(item.specifications) && item.specifications.length > 0 ? (
                                        //     <ul className='text-sm list-disc ml-5'>
                                        //         {item.specifications.map((spec: any, specIndex: number) => (
                                        //             <li key={specIndex}>
                                        //                 <strong>{spec.name}:</strong>
                                        //                 <ul className='ml-5'>
                                        //                     {spec.options.map((option: any, optionIndex: number) => (
                                        //                         <li key={optionIndex}>
                                        //                             {option.option_name} - {displayPrice(option.extra_cost)}
                                        //                         </li>
                                        //                     ))}
                                        //                 </ul>
                                        //             </li>
                                        //         ))}
                                        //     </ul>
                                        // ) : (
                                        //     <p>&nbsp;</p>
                                        // )
                                    }


                                    <div className='flex justify-between font-semibold py-2'>
                                        <p>Unit Price: {settings?.currencyName}{displayPrice(calculateItemPrice(item))}</p>
                                        <p>Quantity: {item.quantity}</p>
                                        <p>Price: {settings?.currencyName}{displayPrice(item.total_price)}</p>
                                    </div>
                                </li>
                            ))
                        ) : (
                            <p>No items available in this order.</p>
                        )}
                    </ol>

                    {(order.status === 'paid') && (
                        <div className="pb-16 mx-1 md:mx-5">
                            <div className="text-grey-800 text-sm p-4 m-4 mt-0 border-2 border-dashed rounded-md shadow-md bg-grey-400">

                                <div className="order-details">
                                    <h3 className='font-bold'>PAID</h3>
                                    <p>Order ID: {order.id}</p>
                                    {/* <p>Vendor ID: {order.vendor_id}</p> */}
                                    <p>Order Type: {order.order_type}</p>
                                    <p>Table: {order.table?.table_name} at {order.table?.location}</p>
                                    <p>Status: {order.status}</p>
                                    <p>Amount to Pay: {settings?.currencyName}{displayPrice(order.amount_to_pay)}</p>
                                    <p>Total Taxes: {settings?.currencyName}{displayPrice(order.total_taxes)}</p>
                                    <p>Receivable: {settings?.currencyName}{displayPrice(order.receivable)}</p>
                                    <p>Rounding Value: {settings?.currencyName}{displayPrice(order.rounding_value)}</p>
                                    <p>Rounded Receivable: {settings?.currencyName}{displayPrice(order.rounded_receivable)}</p>
                                    <p>Received Amount: {settings?.currencyName}{displayPrice(order.received_amount)}</p>
                                    <p>Change: {settings?.currencyName}{displayPrice(order.change)}</p>

                                    <p>Created At: {new Date(order.created_at).toLocaleString()}</p>
                                    <p>Updated At: {new Date(order.updated_at).toLocaleString()}</p>
                                </div>
                            </div>
                        </div>
                    )}

                </div>


                <div className='orderdetails-summary-part'>
                    <div className='mb-1'>
                        <p>Amount to Pay: {displayPrice(amountToPay, settings?.currencyName)}</p>
                        <p>{firstTax?.name} ({firstTax?.taxAmount}%) Tax Amount: {displayPrice(calculatedTax, settings?.currencyName)}</p>
                        <p>Total Amount: {displayPrice(calculatedTotal, settings?.currencyName)}</p>
                    </div>

                    <h5 className='mb-2 '>Amount To Pay: {displayPrice(calculatedTotal, settings?.currencyName)}</h5>

                    <div className='flex flex-row justify-center md:justify-end my-2'>

                        <div className="mx-1 md:mx-5">
                            <button
                                onClick={() => {

                                    handleIsModalVisible();
                                }}
                                className="MoreBtn w-20"
                                style={{ backgroundColor: '#7a75b5' }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <BsThreeDots />&nbsp;
                                </div>
                            </button>


                        </div>

                        {(order.status === 'pending') && (

                            <div className="mx-1 md:mx-5">
                                <button
                                    onClick={() => {
                                        // Prompt the user for confirmation
                                        // const isConfirmed = window.confirm('Please confirm that Extra Order will clear the current cart');

                                        // if (isConfirmed) {
                                        // Clear the cart (assumes you have a function like clearCart to manage this)
                                        clearCart(); // Replace with your actual cart-clearing logic

                                        // Set the orderId in session storage
                                        localStorage.setItem('orderId', orderId);
                                        localStorage.setItem('currSelectedTable', order.table?.table_name || '');

                                        // Navigate to the ExtraOrder page with the orderId in the query string

                                        // navigate('/cart?orderId=' + orderId);

                                        navigate('/');

                                        // }
                                    }}
                                    className="MoreBtn w-20"
                                >
                                    Add Order

                                </button>
                            </div>
                        )}

                        {(order.status === 'pending') && (
                            <CancelOrderButton orderId={orderId as number} currentTable={order.table as TableDetails | null} />
                        )}

                        {(order.status === 'pending') && (
                            <div className="mx-1 md:mx-5">
                                <button onClick={() => navigate('/payment/' + orderId + '')} className="GoBtn w-20">
                                    Pay
                                </button>
                            </div>
                        )}

                        {/* {(order.status === 'paid') && (
                            <div className="mx-1 md:mx-5">
                                <button className="ActionBtn bg-red-800">
                                    Order {order.id} Paid
                                </button>
                            </div>
                        )} */}

                        {/* {(order.status === 'cancel') && (
                            <div className="mx-1 md:mx-5">
                                <button className="ActionBtn bg-red-800">
                                    Order {order.id} Cancelled
                                </button>
                            </div>
                        )} */}

                        {/* <PayOrderButton orderId={orderId as number}  /> */}

                    </div>
                </div>
            </div>



            {/* Modal */}

            {isModalVisible && (
                ReactDOM.createPortal(
                    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50"
                        onClick={closeIsModalVisible} // Close modal when clicking outside
                    >
                        <div className="bg-white rounded-lg shadow-lg p-4 relative w-96"
                            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
                        >
                            {/* Close button */}
                            <button
                                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                                onClick={closeIsModalVisible}
                            >
                                ✖
                            </button>
                            {/* PrintReceipt Component */}
                            <MoreOrderNavModal orderId={orderId} onPrintButtonClick={handlePrintButtonClick} />

                        </div>
                    </div>,
                    document.body
                )

            )}

            {/* Modal */}
            {printModalOpen && (
                ReactDOM.createPortal(
                    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50"
                        onClick={handleCloseModal}
                    >
                        <div className="bg-white rounded-lg shadow-lg p-4 relative w-96"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close button */}
                            <button
                                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                                onClick={handleCloseModal}
                            >
                                ✖
                            </button>
                            {/* PrintReceipt Component */}
                            <PrintReceipt orderId={orderId} />
                        </div>
                    </div>,
                    document.body
                )
            )}
        </div>
    );
};

export default OrderDetails;
